<script lang="ts">
  import type {
    IFormPrivacyPolicyNode,
    PartialNode,
  } from "@shared/common/node/type";
  import { generateFormDataName } from "@shared/common/utils/form";
  import { stringTemplateHelper } from "@shared/common/utils/string-template-helper";
  import { onMount } from "svelte";
  import detectReaction from "../../actions/detect-reaction";
  import {
    getCurrentDocumentNode,
    getFormContext,
    getOrInitCheckboxItem,
    getStore,
  } from "../../context";
  import { simpleRandomId } from "../../util/common";
  import FormCheckbox from "./form/form-checkbox.svelte";

  const { reactionEventListener, dataMap, t } = getStore();

  const { checkboxMap } = getFormContext();
  const documentNode = getCurrentDocumentNode();

  export let node: PartialNode<IFormPrivacyPolicyNode>;

  $: name = generateFormDataName(node);

  const id = simpleRandomId();

  onMount(() => {
    getOrInitCheckboxItem(checkboxMap, name).required = node.required || false;
  });

  function onKeyDownLabel(e: KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      document.getElementById(id)?.click();
    }
  }

  function onClickLabel(e: MouseEvent) {
    e.preventDefault();
    document.getElementById(id)?.click();
  }
</script>

<details
  use:detectReaction={{
    node,
    documentNode,
    onAction: reactionEventListener,
  }}
  class="privacy"
>
  <summary class="privacy-title">
    <FormCheckbox {name} value="ok" {id} />
    <label
      role="presentation"
      for={id}
      class="privacy-title-label"
      on:keydown={onKeyDownLabel}
      on:click={onClickLabel}
    >
      <p style="display:flex;flex-wrap:wrap;">
        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
        {@html stringTemplateHelper({ mode: "VIEW", t: $t }).render(
          node.label || "",
          $dataMap
        )}
        {#if node.required}
          <span class="privacy-label-required">
            ({$t("common.inputRequired")})
          </span>
        {/if}
      </p>
    </label>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="privacy-down-button"><path d="m6 9 6 6 6-6" /></svg
    >
  </summary>
  <div class="privacy-content">
    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
    {@html stringTemplateHelper({ mode: "VIEW", t: $t }).render(
      node.content || "",
      $dataMap
    )}
  </div>
</details>

<style>
  .privacy {
    font-size: var(--cb-text-md);
    line-height: var(--cb-line-height-md);
    color: rgba(var(--cb-form-font-color), 1);
    text-align: left;
  }

  .privacy-title::-webkit-details-marker {
    display: none;
  }

  /* 아이폰 사파리에서 summary::marker를 숨기기 위한 스타일 */
  .privacy summary::marker {
    font-size: 0;
    position: absolute;
  }

  .privacy-title {
    display: flex;
    align-items: center;
    font-weight: 600;
    justify-content: space-between;
  }

  .privacy-title-label {
    padding-left: calc(var(--cb-form-font-size) * 0.5);
    flex: 1 1 0%;
    word-break: break-all;
    cursor: default;
  }

  .privacy-down-button {
    display: block;
    box-sizing: content-box;
    width: calc(var(--cb-form-font-size) * 1.25);
    height: calc(var(--cb-form-font-size) * 1.25);
    padding: calc(var(--cb-form-font-size) * 0.25);
    border-radius: 50%;
    cursor: pointer;
    color: rgba(var(--cb-form-font-color), 0.7);
    transition-property: transform, opacity, background-color;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
  }

  .privacy-down-button:hover,
  summary:focus .privacy-down-button {
    background-color: rgba(var(--cb-form-primary-color), 0.1);
  }

  .privacy[open] .privacy-down-button {
    transform: rotate(180deg);
  }

  .privacy-content {
    margin-top: calc(var(--cb-form-font-size) * 0.5);
    color: rgba(var(--cb-form-font-color), 1);
    user-select: initial;
    font-size: var(--cb-text-base);
    line-height: var(--cb-line-height-base);
    background-color: white;
  }

  .privacy-content :global(ul li) {
    padding-left: 1.5em;
  }

  .privacy-content :global(ul li::before) {
    content: "\2022";
    margin-left: -1.5em;
    margin-right: 0.3em;
    text-align: right;
    display: inline-block;
    white-space: nowrap;
    width: 1.2em;
  }

  .privacy-label-required {
    font-weight: lighter;
    margin-left: calc(var(--cb-form-font-size) * 0.2);
  }
</style>
